import { generateGUID } from "@faro-lotv/foundation";
import { clearStore } from "@faro-lotv/project-source";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { clearUserEdits } from "../data-preparation-actions";
import {
  DEFAULT_EDIT_MODE,
  RegistrationEditMode,
  UserEdit,
  UserEditAddConnection,
  UserEditDeleteConnection,
  UserEditTransformScans,
  UserEditType,
} from "../user-edits/user-edit-types";

type DataPreparationEditingState = {
  /** The currently active edit mode. */
  editMode?: RegistrationEditMode;

  /** The unsaved edits performed by the user. */
  userEdits: UserEdit[];

  /** The undid edits*/
  undidUserEdits: UserEdit[];

  /**
   * The transform a user is currently applying to scans.
   *
   * Separated for performance reasons, so that ongoing edits do not require recomputation of all selectors.
   */
  inProgressTransformEdit?: UserEditTransformScans;
};

export const DATA_PREPARATION_EDITING_STATE_INITIAL_STATE: Readonly<DataPreparationEditingState> =
  Object.freeze({
    userEdits: [],
    undidUserEdits: [],
    inProgressTransformEdit: undefined,
  });

const dataPreparationEditingStateSlice = createSlice({
  initialState: DATA_PREPARATION_EDITING_STATE_INITIAL_STATE,
  name: "editing-state",

  reducers: {
    /**
     * Enable editing and switch to the default editing mode.
     *
     * @param state The current application state
     */
    enableDefaultEditMode(state) {
      state.editMode = DEFAULT_EDIT_MODE;
    },

    /**
     * Disable any active edit mode, making the view read-only again.
     *
     * @param state The current application state
     */
    disableEditMode(state) {
      state.editMode = undefined;
    },

    /**
     * Set the status of the edit mode
     *
     * @param state The current application state
     * @param action The new active edit mode
     */
    setEditMode(state, action: PayloadAction<RegistrationEditMode>) {
      state.editMode = action.payload;
    },

    /**
     * Adds the id of the user added connection to the UserEdits array
     *
     * @param state The current application state
     * @param action The connection the user added manually
     */
    addUserEditAddConnection(
      state,
      action: PayloadAction<
        Pick<UserEditAddConnection, "sourceId" | "targetId" | "createdBy">
      >,
    ) {
      const addConnection: UserEditAddConnection = {
        ...action.payload,
        type: UserEditType.addConnection,
        id: generateGUID(),
        createdAt: new Date().toISOString(),
      };
      state.userEdits.push(addConnection);

      // Clear redo stack since new edits change the edit history
      state.undidUserEdits = [];
    },

    /**
     * Adds the id of the user deleted connection to the UserEdits array
     *
     * @param state The current application state
     * @param action The connection the user deleted manually
     */
    addUserEditDeleteConnection(
      state,
      action: PayloadAction<
        Pick<UserEditDeleteConnection, "sourceId" | "targetId" | "createdBy">
      >,
    ) {
      const deleteConnection: UserEditDeleteConnection = {
        ...action.payload,
        type: UserEditType.deleteConnection,
        id: generateGUID(),
        createdAt: new Date().toISOString(),
      };
      state.userEdits.push(deleteConnection);

      // Clear redo stack since new edits change the edit history
      state.undidUserEdits = [];
    },

    /**
     * Sets the state of the current in-progress transform edit.
     * After the edit is completed, {@link commitInProgressTransformEdit} must be called to finalize the edit.
     *
     * @param state The current application state
     * @param action The state of the in-progress transform edit
     */
    updateInProgressTransformEdit(
      state,
      action: PayloadAction<
        Pick<
          UserEditTransformScans,
          "scanIds" | "newPoses" | "mode" | "cachedWorldTransforms"
        >
      >,
    ) {
      const { scanIds, newPoses, mode, cachedWorldTransforms } = action.payload;

      const moveScans: UserEditTransformScans = {
        type: UserEditType.moveScans,
        mode,
        id: generateGUID(),
        createdAt: new Date().toISOString(),
        scanIds,
        newPoses,
        cachedWorldTransforms,
      };
      state.inProgressTransformEdit = moveScans;

      // Clear redo stack since new edits change the edit history
      state.undidUserEdits = [];
    },

    /**
     * Commits the in-progress transform edit after completion.
     * Must be called after {@link updateInProgressTransformEdit} to complete the edit.
     *
     * @param state The current application state
     */
    commitInProgressTransformEdit(state) {
      if (state.inProgressTransformEdit) {
        state.userEdits.push(state.inProgressTransformEdit);
        state.inProgressTransformEdit = undefined;
      }
    },

    /**
     * Removes the last edit entry from the UserEdits array
     *
     * @param state The current application state
     */
    undoLastUserEdit(state) {
      const lastEdit = state.userEdits.pop();
      if (lastEdit) {
        state.undidUserEdits.push(lastEdit);
      }
    },

    /**
     * Adds the last undid edit entry to the UserEdits array
     *
     * @param state The current application state
     */
    redoLastUserEdit(state) {
      const lastEdit = state.undidUserEdits.pop();
      if (lastEdit) {
        state.userEdits.push(lastEdit);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      clearStore,
      () => DATA_PREPARATION_EDITING_STATE_INITIAL_STATE,
    );

    builder.addCase(clearUserEdits, (state) => {
      state.userEdits = [];
      state.undidUserEdits = [];
      state.inProgressTransformEdit = undefined;
    });
  },
});

export const {
  enableDefaultEditMode,
  disableEditMode,
  setEditMode,
  addUserEditAddConnection,
  addUserEditDeleteConnection,
  updateInProgressTransformEdit,
  commitInProgressTransformEdit,
  undoLastUserEdit,
  redoLastUserEdit,
} = dataPreparationEditingStateSlice.actions;

export const dataPreparationEditingStateReducer =
  dataPreparationEditingStateSlice.reducer;
